import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api, publicFileUrl } from "@/lib/api";
import { Trophy, ArrowLeft, Award } from "lucide-react";
import GironeBadge from "@/components/GironeBadge";
import SoccerPitch from "@/components/SoccerPitch";
import PlayerJersey from "@/components/PlayerJersey";
import { TeamPageSkeleton } from "@/components/Skeleton";
import { slotsForModule } from "@/lib/modules";

const SHIELD_PLACEHOLDER = "https://images.unsplash.com/photo-1577214322326-5b31d9d886b0?crop=entropy&cs=srgb&fm=jpg&w=400&q=80";
const COACH_PLACEHOLDER = "https://images.unsplash.com/photo-1762505031623-1e9a2f31858e?crop=entropy&cs=srgb&fm=jpg&w=400&q=80";
const JERSEY_PLACEHOLDER = "https://images.unsplash.com/photo-1552066379-e7bfd22155c5?crop=entropy&cs=srgb&fm=jpg&w=400&q=80";

const ROLE_COLORS = { PT: "#f59e0b", DEF: "#06b6d4", CEN: "#3b56e0", ATT: "#f97316" };
const ROLE_LABEL = { PT: "Portieri", DEF: "Difensori", CEN: "Centrocampisti", ATT: "Attaccanti" };

export default function TeamPage() {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [ach, setAch] = useState(null);

  useEffect(() => {
    api.get(`/public/teams/${userId}`).then(({ data }) => setData(data))
      .catch((e) => setErr(e.response?.data?.detail || "Squadra non trovata"));
    // Trofei del fantallenatore
    api.get(`/public/achievements/${userId}`).then(({ data }) => setAch(data)).catch(() => setAch(null));
  }, [userId]);

  if (err) return (
    <div className="App"><Header />
      <div className="max-w-4xl mx-auto px-4 py-16 text-center text-slate-500" data-testid="team-not-found">{err}</div>
    </div>
  );
  if (!data) return (
    <div className="App"><Header />
      <TeamPageSkeleton />
    </div>
  );

  const byId = Object.fromEntries((data.roster || []).map((p) => [p.id, p]));
  const slots = slotsForModule(data.lineup?.module || "442");
  const byRole = { PT: [], DEF: [], CEN: [], ATT: [] };
  (data.roster || []).forEach((p) => byRole[p.role]?.push(p));

  return (
    <div className="App">
      <Header />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 space-y-6">
        <Link to="/squadre" className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-900 text-sm" data-testid="back-to-squadre">
          <ArrowLeft className="w-4 h-4" /> Tutte le squadre
        </Link>

        {/* Header team */}
        <div className="card-surface rounded-3xl p-6 md:p-8 flex items-center gap-6 flex-wrap fade-up">
          <img src={data.shield_path ? publicFileUrl(data.shield_path) : SHIELD_PLACEHOLDER}
               onError={(e) => { e.currentTarget.src = SHIELD_PLACEHOLDER; }}
               alt="Stemma" className="w-28 h-28 md:w-32 md:h-32 rounded-2xl object-cover" data-testid="team-shield" />
          <div className="flex-1 min-w-[220px]">
            <div className="text-xs uppercase tracking-widest text-slate-500 inline-flex items-center gap-1.5">
              <GironeBadge girone={data.girone} size={16} />
              <span>Girone · Stagione 2026/27</span>
            </div>
            <h1 className="display font-black text-4xl md:text-5xl mt-1" data-testid="team-page-name">{data.team_name}</h1>
            <div className="text-slate-500 mt-1">Fantallenatore: <b className="text-slate-900">{data.coach_name}</b></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Stat label="Punti totali" value={data.points.toFixed(1)} accent="var(--brand)" />
            <Stat label="Giornate" value={data.played} accent="#06b6d4" />
          </div>
        </div>

        {/* Identity */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <MiniCard label="Maglia" src={data.jersey_path ? publicFileUrl(data.jersey_path) : JERSEY_PLACEHOLDER} />
          <MiniCard label="Fantallenatore" src={data.coach_path ? publicFileUrl(data.coach_path) : COACH_PLACEHOLDER} />
          <div className="card-surface rounded-2xl p-5">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Modulo attuale</div>
            <div className="display font-black text-4xl mt-1" data-testid="team-page-module">{data.lineup?.module || "-"}</div>
            <div className="text-slate-500 text-sm mt-2">Formazione tipo salvata dal fantallenatore.</div>
          </div>
        </div>

        {/* Trofei del fantallenatore */}
        {ach && ach.unlocked && ach.unlocked.length > 0 && (
          <div className="card-surface rounded-2xl p-5 md:p-6" data-testid="team-achievements">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full flex items-center justify-center"
                   style={{ background: "linear-gradient(135deg, #fbbf24, #dc2626)" }}>
                <Award className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Bacheca trofei</div>
                <div className="display font-black text-lg">
                  {ach.unlocked.length} trofei sbloccati · {ach.locked_count} da conquistare
                </div>
              </div>
            </div>
            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-3">
              {ach.unlocked.map((a) => {
                const tierColors = {
                  legend: { bg: "linear-gradient(135deg, #fbbf24, #f59e0b, #dc2626)", ring: "#f59e0b", txt: "#78350f" },
                  gold:   { bg: "linear-gradient(135deg, #fde68a, #f59e0b)",           ring: "#f59e0b", txt: "#78350f" },
                  silver: { bg: "linear-gradient(135deg, #e2e8f0, #94a3b8)",           ring: "#94a3b8", txt: "#334155" },
                  bronze: { bg: "linear-gradient(135deg, #fed7aa, #c2410c)",           ring: "#c2410c", txt: "#7c2d12" },
                };
                const t = tierColors[a.tier] || tierColors.bronze;
                return (
                  <div key={a.id} className="rounded-xl p-2 border-2 text-center bg-white transition-all hover:scale-105"
                       style={{ borderColor: t.ring }}
                       title={`${a.name} · ${a.description}`}
                       data-testid={`team-badge-${a.id}`}>
                    <div className="w-11 h-11 mx-auto rounded-full flex items-center justify-center text-xl"
                         style={{ background: t.bg }}>
                      {a.icon}
                    </div>
                    <div className="display font-black text-[10px] mt-1 leading-tight" style={{ color: t.txt }}>
                      {a.name}
                    </div>
                    <div className="text-[9px] uppercase tracking-widest mt-0.5" style={{ color: t.ring }}>
                      {a.tier}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Formation */}
        <div className="card-surface rounded-3xl p-4 md:p-6">
          <div className="flex items-center gap-2 mb-3">
            <Trophy className="w-4 h-4" style={{ color: "var(--brand)" }} />
            <div className="display font-bold text-lg">Formazione tipo · {data.lineup?.module || "—"}</div>
          </div>
          <div className="max-w-md mx-auto">
            <SoccerPitch>
              {slots.map((s, i) => {
                const pid = (data.lineup?.starters?.[s.role] || [])[s.index];
                const p = pid ? byId[pid] : null;
                return (
                  <div key={i} style={{ left: `${s.x}%`, top: `${s.y}%` }}
                       className="absolute -translate-x-1/2 -translate-y-1/2">
                    <PlayerJersey player={p} role={s.role} size={42} empty={!p} />
                  </div>
                );
              })}
            </SoccerPitch>
          </div>
        </div>

        {/* Roster con costi (dati dall'asta) */}
        <div className="card-surface rounded-3xl p-4 md:p-6" data-testid="team-roster">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Rosa completa</div>
              <div className="display font-black text-xl md:text-2xl">
                {data.roster?.length || 0} giocatori
              </div>
            </div>
            {typeof data.total_cost === "number" && data.total_cost > 0 && (
              <div className="rounded-2xl px-4 py-2 text-right" style={{ background: "var(--brand)18", border: "1px solid var(--brand)44" }} data-testid="team-total-cost">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Spesa totale asta</div>
                <div className="display font-black text-2xl" style={{ color: "var(--brand)" }}>{data.total_cost} <span className="text-sm text-slate-500 font-bold">crediti</span></div>
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {["PT", "DEF", "CEN", "ATT"].map((r) => {
              const arr = byRole[r] || [];
              const roleTotal = arr.reduce((s, p) => s + (Number(p.cost) || 0), 0);
              return (
                <div key={r} className="rounded-2xl border border-slate-200 overflow-hidden" data-testid={`roster-role-${r}`}>
                  <div className="px-3 py-2 flex items-center justify-between" style={{ background: `${ROLE_COLORS[r]}18` }}>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-black px-1.5 py-0.5 rounded" style={{ background: `${ROLE_COLORS[r]}33`, color: "#0f172a" }}>{r}</span>
                      <span className="display font-bold text-sm">{ROLE_LABEL[r]}</span>
                      <span className="text-[10px] text-slate-500">({arr.length})</span>
                    </div>
                    {roleTotal > 0 && (
                      <span className="text-[10px] font-bold text-slate-600">{roleTotal} cr.</span>
                    )}
                  </div>
                  <div className="divide-y divide-slate-100">
                    {arr.length === 0 && (
                      <div className="px-3 py-3 text-slate-400 text-xs italic">Nessun giocatore</div>
                    )}
                    {arr.map((p) => {
                      const c = Number(p.cost) || 0;
                      const isTop = c >= 30;
                      return (
                        <div key={p.id || p.name} className="px-3 py-2 flex items-center justify-between gap-2" data-testid={`roster-player-${p.name}`}>
                          <span className="text-sm text-slate-800 truncate">{p.name}</span>
                          {c > 0 ? (
                            <span
                              className={`text-[11px] font-black tabular-nums px-2 py-0.5 rounded-full shrink-0 ${isTop ? "text-white" : "text-slate-700 bg-slate-100 border border-slate-200"}`}
                              style={isTop ? { background: "var(--orange)" } : undefined}
                              title="Costo asta"
                            >
                              {c}
                            </span>
                          ) : (
                            <span className="text-[10px] text-slate-400 shrink-0">svinc.</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Matchday chart */}
        {data.matchday_points?.length > 0 && (
          <div className="card-surface rounded-3xl p-6">
            <div className="display font-bold text-lg mb-4">Andamento giornate</div>
            <div className="grid grid-cols-6 md:grid-cols-12 gap-2">
              {data.matchday_points.map((m) => (
                <div key={m.number} className="rounded-xl bg-slate-50 border border-slate-200 p-2 text-center">
                  <div className="text-[10px] text-slate-500">G{m.number}</div>
                  <div className="display font-black" style={{ color: "var(--brand)" }}>{m.points.toFixed(1)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <Footer />

    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-2xl px-4 py-3" style={{ background: `${accent}12`, border: `1px solid ${accent}33` }}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="display font-black text-2xl" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function MiniCard({ label, src }) {
  return (
    <div className="card-surface rounded-2xl overflow-hidden">
      <div className="aspect-video bg-slate-50">
        <img src={src} alt={label} className="w-full h-full object-cover" />
      </div>
      <div className="p-3 text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
    </div>
  );
}
